import categoryBreadCrumbs, { type CategoryBreadCrumbs } from '~/helpers/utils/categoryBreadCrumbs';
import { useProduct } from '~/composables/custom/useProduct/useProduct';
import { urlGetters } from '~/composables/getters';

export const useBreadcrumbs = () => {
  const breadcrumbs = useState<CategoryBreadCrumbs[]>(() => []);
  const breadcrumbsToDisplay = useState(() => []);

  const route = useRoute();
  const { result: categories } = useCategories();
  const { result: category } = useCategory();
  const { product } = useProduct(route.params.id as string);
  const { $i18n } = useNuxtApp();

  const { isSale } = route.meta;

  const getCatLink = (slugs: string[]): string => {
    const { query, meta } = route;

    const getNavigationUrl = urlGetters.getCategoryNavigationByMeta(meta, $i18n);

    if (!query) {
      return `${getNavigationUrl}/${slugs.join('/')}`;
    }
    return `${getNavigationUrl}/${slugs.join('/')}?${Object.entries(query).map(([key, value]) => `${key}=${value}`).join('')}`;
  };

  const build = () => {
    const {
      slug_1: slug1,
      slug_2: slug2,
      slug_3: slug3,
      slug_4: slug4,
    } = route.params;

    const currentSlugs = [slug4, slug3, slug2, slug1].filter(item => !item?.includes('c_')).filter(Boolean) as string[];

    let value = route.query.categoryId as string;
    let property = 'id';

    if (!value) {
      [value] = currentSlugs;
      property = 'slug';
    }

    breadcrumbs.value = categoryBreadCrumbs(categories.value, property, value) || [];

    breadcrumbsToDisplay.value = breadcrumbs.value.map(({ name, slug }, i) => {
      const slugs = [breadcrumbs.value[0].slug];

      if (i > 0) {
        slugs.push(slug);
      }

      return ({
        index: i,
        text: name,
        link: getCatLink(slugs),
      });
    });
  };

  const currentBreadcrumb = computed(() => {
    const details = category.value;

    const titlesAndDescriptions = {
      metaTitle: details?.metaTitle,
      metaDescription: details?.metaDescription,
      headlineTitle: details?.headlineTitle ?? details?.name,
      description: details?.description ?? details?.name,
    };

    if (isSale) {
      const saleTranslation = $i18n.t('Sale');
      titlesAndDescriptions.metaTitle = details?.saleMetadata.metaTitle ?? `${saleTranslation}: ${titlesAndDescriptions.headlineTitle}`;
      titlesAndDescriptions.metaDescription = details?.saleMetadata.metaDescription ?? `${saleTranslation}: ${titlesAndDescriptions.description}`;
      titlesAndDescriptions.headlineTitle = details?.saleMetadata.headline ?? `${saleTranslation}: ${titlesAndDescriptions.headlineTitle}`;
      titlesAndDescriptions.description = details?.saleMetadata.description; // no fallback needed
    }

    return {
      ...titlesAndDescriptions,
      canonicalUrl: details?.canonicalUrl,
      category: (categories.value?.[0] ? breadcrumbs.value?.find(({ id }) => id === categories.value?.[0].id) : details),
    };
  });

  return {
    breadcrumbsToDisplay: computed(() => {
      return [...breadcrumbsToDisplay.value, ...(product.value?.name ? [{ text: product.value?.name }] : [])];
    }),
    currentBreadcrumb: computed(() => currentBreadcrumb.value),
    breadcrumbs: computed(() => breadcrumbs.value),
    build,
  };
};
